import type { PaymentCard } from '@api/types';
import type { OfferProduct, PaymentMethods } from '@api/types';
import bitpaceImg from '@images/billing/bitpace.svg';
import americanExpressCardImg from '@images/billing/card/amex.svg';
import dinersCardImg from '@images/billing/card/diners-club.svg';
import discoverCardImg from '@images/billing/card/discover.svg';
import jsbCardImg from '@images/billing/card/jcb.svg';
import maestroCardImg from '@images/billing/card/maestro.svg';
import masterCardImg from '@images/billing/card/mastercard.svg';
import otherCardImg from '@images/billing/card/other.svg';
import ruPayCardImg from '@images/billing/card/ru-pay.svg';
import unionPayCardImg from '@images/billing/card/union-pay.svg';
import visaCardImg from '@images/billing/card/visa.svg';
import centroBillImg from '@images/billing/centroBill.svg';
import epochImg from '@images/billing/epoch.svg';
import priorityBillImg from '@images/billing/priority-bill.svg';
import { PaymentCardBrand, PaymentType } from '@shared/types';
import { Billing, PaymentMethod } from '@shared/types';
import { ProductType } from '@shared/types/invoice';

import type { PaymentMethodWithAlias } from './types';

export function getBillingUnsubscribeUrl(billing: Billing): string {
    return {
        [Billing.CentroBill]: 'https://centrohelp.com',
        [Billing.Epoch]: 'https://epoch.com/billing_support/find_purchase',
        [Billing.UpGate]: 'https://prioritybill.com/',
        [Billing.PayGarden]: '',
        [Billing.Bitpace]: 'https://www.bitpace.com/',
    }[billing];
}

export function getBillingCardBrand(brand: PaymentCardBrand | PaymentCard['brand'] | ''): string {
    return {
        [PaymentCardBrand.AmericanExpress]: americanExpressCardImg,
        [PaymentCardBrand.Diners]: dinersCardImg,
        [PaymentCardBrand.Jsb]: jsbCardImg,
        [PaymentCardBrand.Maestro]: maestroCardImg,
        [PaymentCardBrand.Mastercard]: masterCardImg,
        [PaymentCardBrand.Visa]: visaCardImg,
        [PaymentCardBrand.UnionPay]: unionPayCardImg,
        [PaymentCardBrand.RuPay]: ruPayCardImg,
        [PaymentCardBrand.Discover]: discoverCardImg,
        ['']: otherCardImg,
    }[brand];
}

export function getBillingUrl(billing: Billing): string {
    return {
        [Billing.CentroBill]: 'https://centrobill.com',
        [Billing.Epoch]: 'https://epoch.com',
        [Billing.UpGate]: 'https://prioritybill.com/',
        [Billing.PayGarden]: '',
        [Billing.Bitpace]: 'https://www.bitpace.com/',
    }[billing];
}

export function getBillingIcon(billing: Billing): string {
    return {
        [Billing.CentroBill]: centroBillImg,
        [Billing.Epoch]: epochImg,
        [Billing.UpGate]: priorityBillImg,
        [Billing.PayGarden]: epochImg,
        [Billing.Bitpace]: bitpaceImg,
    }[billing];
}

export function getEventPaymentType(productType: ProductType): PaymentType | undefined {
    switch (productType) {
        case ProductType.Subscription:
            return PaymentType.Membership;
        case ProductType.TimePackage:
            return PaymentType.ExtraMinutes;
        default:
            return;
    }
}

/**
 * Utility to calculate product total price for a period
 */
export function getTotalPrice(price: number, period?: number): number {
    return price * ((period || 30) / 30);
}

export function checkIfCryptoEligibleForSome(
    isTrial: boolean,
    paymentMethodName: PaymentMethod | undefined,
    availableProducts: OfferProduct[],
    cryptoBillings: PaymentMethods[0]['billings']
): boolean {
    if (isTrial || paymentMethodName !== PaymentMethod.Crypto) {
        return false;
    }

    return availableProducts.some(product => isSomeCryptoBillingAvailableFor(product, cryptoBillings));
}

export function trySelectProductEligibleForCryptoPayment(
    isTrial: boolean,
    paymentMethod: PaymentMethodWithAlias,
    selectedProductId: OfferProduct['id'] | null,
    visibleProducts: OfferProduct[],
    visiblePaymentMethods: PaymentMethods,
    skipCallback: () => void,
    applyCallback: (productId: OfferProduct['id']) => void
): void {
    const cryptoMethod = visiblePaymentMethods.find(pm => pm.paymentMethod === PaymentMethod.Crypto);
    if (!cryptoMethod ||
        !selectedProductId ||
        !checkIfCryptoEligibleForSome(isTrial, paymentMethod?.name, visibleProducts, cryptoMethod.billings)) {
        skipCallback();
        return;
    }

    // find all crypto payable products
    const cryptoPayable = visibleProducts
        .filter(product => isSomeCryptoBillingAvailableFor(product, cryptoMethod.billings));

    // in case currently selected product is in the list of payable options do nothing
    if (cryptoPayable.some(p => p.id === selectedProductId)) {
        skipCallback();
        return;
    }

    const cheapestCryptoPayableProduct = cryptoPayable.length === 0
        ? null
        : cryptoPayable.reduce((min, curr) =>
            getTotalPrice(curr.price, curr.period) < getTotalPrice(min.price, min.period) ? curr : min);

    if (cheapestCryptoPayableProduct) {
        applyCallback(cheapestCryptoPayableProduct.id);
    }
}

export function tryUnselectCryptoIfProductTooCheap(
    isTrial: boolean,
    currentMethod: PaymentMethodWithAlias | null,
    selectedProductId: OfferProduct['id'] | null,
    visibleProducts: OfferProduct[],
    visiblePaymentMethods: PaymentMethods,
    applyCallback: (newMethod: PaymentMethodWithAlias) => void
): void {
    const cryptoPayment = visiblePaymentMethods.find(pm => pm.paymentMethod === PaymentMethod.Crypto);
    if (!cryptoPayment ||
        !selectedProductId ||
        !checkIfCryptoEligibleForSome(isTrial, currentMethod?.name, visibleProducts, cryptoPayment?.billings)) {
        return;
    }

    const selectedProduct = visibleProducts.find(s => s.id === selectedProductId);
    const noCryptoBillingsForProduct = selectedProduct &&
                    !isSomeCryptoBillingAvailableFor(selectedProduct, cryptoPayment.billings);
    const nonCryptoMethod  = visiblePaymentMethods.find(pm => pm !== cryptoPayment);

    if (noCryptoBillingsForProduct && nonCryptoMethod) {
        applyCallback({
            name: nonCryptoMethod.paymentMethod as PaymentMethod,
            alias: nonCryptoMethod.aliases?.[0],
            billing: nonCryptoMethod.billings[0].billing as Billing,
        });
    }
}

function isSomeCryptoBillingAvailableFor(
    product: OfferProduct,
    cryptoBillings: PaymentMethods[0]['billings']
): boolean {
    const productTotalPrice = getTotalPrice(product.price, product.period);

    if (!product.pricePerMinute) {
        return false;
    }

    return cryptoBillings.some(b => {
        if (b.productPriceMin) {
            if (productTotalPrice < b.productPriceMin) {
                return false;
            }
        }

        return product.paymentSettings && !product.paymentSettings.some((s) => {
            return s.excludedPaymentMethods?.includes(PaymentMethod.Crypto) && s.billing === b.billing;
        });
    });
}
